import { PiArrowLeftBold, PiCopyBold, PiCheckBold } from 'react-icons/pi';
import { Button } from '@ewf/components/Button';
import { SunkenBox } from '@ewf/components/SunkenBox';
import { CopyAddressButton } from '@ewf/components/ModalUI/CopyAddressButton';
import { CloseButton } from '@ewf/components/ModalUI/CloseButton';
import { Stepper } from '@ewf/components/ModalUI/Stepper';
import { TransactionProgress } from '@ewf/components/ModalUI/TransactionProgress';
import { formatGasFee } from '@ewf/utils/balance-helpers';
import { truncateAddress } from '@ewf/lib/utils';
import { MARKETPLACE_CHAINS } from '@shared/types/enums';
import ethLogo from '@ewf/assets/logos/eth.svg';
import { useState } from 'react';

export const Confirm = ({
  fee,
  amount,
  connectedAddress = '',
  recipientAddress = '',
  onConfirm,
  onGoBack,
  onClose,
}: Props): JSX.Element => {
  const [copied, setCopied] = useState(false);

  const handleCopyRecipient = async () => {
    try {
      await navigator.clipboard.writeText(recipientAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  return (
    <div className="w-[550px] rounded-lg border-2 border-transparent p-8 gradient-border-secondary-with-gray-90">
      <section className="relative mb-8 flex h-[40px] justify-between">
        <button
          onClick={() => onGoBack(amount)}
          className="grid h-[40px] w-[40px] place-items-center rounded-full bg-gray-100 text-font hover:bg-gray-95 active:bg-black"
        >
          <PiArrowLeftBold size={24} />
        </button>
        <CopyAddressButton address={connectedAddress} />
        <CloseButton onClick={onClose} />
      </section>
      <section className="mb-8">
        <Stepper step={2} maxStep={2} />
      </section>
      <section className="mb-8 rounded-lg p-6 bg-popup-gradient">
        <TransactionProgress operation="claim-lowers" step="confirm" />
        <SunkenBox className="mb-6 text-center font-bold">Confirm transaction</SunkenBox>
        <SunkenBox className="w-full">
          <div className="flex items-center justify-between text-font-subtle">
            <p className="text-sm">You claim</p>
            <div className="flex items-center gap-2 text-sm">
              <button
                onClick={handleCopyRecipient}
                className="grid h-[24px] w-[24px] place-items-center rounded bg-gray-90 transition-colors hover:bg-gray-85 active:bg-gray-80"
                title="Copy recipient address"
              >
                {copied ? <PiCheckBold size={14} /> : <PiCopyBold size={14} />}
              </button>
              <span className="font-mono">{truncateAddress(recipientAddress)}</span>
            </div>
            <span className="h-[20px] rounded-lg bg-gradient-to-b from-brand to-teal p-[1px]">
              <span className="grid h-full place-items-center rounded-[15px] bg-gray-80 px-2 text-sm uppercase">
                {MARKETPLACE_CHAINS.ETH}
              </span>
            </span>
          </div>
          <hr className="mb-2 mt-3 border-gray-70" />
          <div className="mb-3 flex items-center justify-start gap-2">
            <img src={ethLogo} width={20} height={20} alt={MARKETPLACE_CHAINS.ETH} />
            <p className="text-lg font-bold">{amount} EWT</p>
          </div>
        </SunkenBox>
      </section>
      <section className="mb-8 flex justify-between rounded-lg p-6 bg-popup-gradient">
        <p>Gas fees</p>
        <p>
          {(() => {
            const formatted = formatGasFee(fee, 'ETH');
            return `${formatted.value} ${formatted.unit}`;
          })()}
        </p>
      </section>
      <section className="flex justify-center">
        <Button onClick={onConfirm}>Confirm</Button>
      </section>
    </div>
  );
};

interface Props {
  fee: number;
  amount: number;
  connectedAddress: string;
  recipientAddress: string;
  onConfirm: () => void;
  onGoBack: (amount: number) => void;
  onClose: () => void;
}
